import { useState, useEffect, useRef } from 'react'
import { Loader } from '@googlemaps/js-api-loader'

export const useGoogleMaps = () => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState(null)
  const autocompleteRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        // Debug: Check what we're getting from env
        console.log('Environment variables:', import.meta.env)
        console.log('API Key from env:', import.meta.env.VITE_GOOGLE_MAPS_API_KEY)
        
        // Use the API key with multiple fallback options
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyApdkpf6nEm8GzGnOHsDgXIZ1tIrI_VXWs'
        
        // For production, ensure we have a valid API key
        if (import.meta.env.PROD && (!apiKey || apiKey === 'your-google-maps-api-key-here')) {
          console.warn('Google Maps API key not configured for production')
          setError('Google Maps unavailable - please use Manual Entry')
          setIsLoaded(false)
          return
        }
        
        console.log('Using API Key:', apiKey)
        
        if (!apiKey || apiKey === 'your-google-maps-api-key-here') {
          throw new Error('Google Maps API key not configured')
        }

        // Add global error handlers for Google Maps
        window.gm_authFailure = () => {
          console.error('Google Maps authentication failed')
          setError('Google Maps API authentication failed. Please check API key and restrictions.')
        }

        // Handle quota exceeded
        window.google_maps_quota_exceeded = () => {
          console.error('Google Maps quota exceeded')
          setError('Google Maps API quota exceeded. Please check your billing and usage limits.')
        }

        // Create the loader with comprehensive configuration
        const loader = new Loader({
          apiKey,
          version: 'weekly',
          libraries: ['places', 'geometry'],
          // Remove domain restrictions for development
          // Add additional options to handle API restrictions
          language: 'en',
          region: 'IN', // Set to India for better local results
          // Add loading options
          loadScriptOptions: {
            async: true,
            defer: true
          }
        })

        console.log('Loading Google Maps API...')
        
        // Load with timeout
        const loadPromise = loader.load()
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Google Maps API loading timeout')), 10000)
        })

        await Promise.race([loadPromise, timeoutPromise])
        
        // Verify Places API is available
        if (!window.google?.maps?.places) {
          throw new Error('Places API not available. Please enable Places API in Google Cloud Console.')
        }

        console.log('Google Maps loaded successfully')
        setIsLoaded(true)
        setError(null)
      } catch (err) {
        console.error('Error loading Google Maps:', err)
        
        // Provide more specific error messages
        let errorMessage = 'Google Maps unavailable - please use Manual Entry'
        
        if (err.message.includes('API key')) {
          errorMessage = 'Invalid API key. Please check your Google Maps API key configuration.'
        } else if (err.message.includes('quota') || err.message.includes('OVER_QUERY_LIMIT')) {
          errorMessage = 'API quota exceeded. Please check your Google Cloud billing and usage limits.'
        } else if (err.message.includes('billing')) {
          errorMessage = 'Billing not enabled. Please enable billing in Google Cloud Console.'
        } else if (err.message.includes('referer') || err.message.includes('domain')) {
          errorMessage = 'Google Maps unavailable - please use Manual Entry'
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Loading timeout. Please check your internet connection.'
        } else if (err.message.includes('Places API')) {
          errorMessage = 'Places API not enabled. Please enable Places API in Google Cloud Console.'
        }
        
        setError(errorMessage)
        setIsLoaded(false)
      }
    }

    initializeGoogleMaps()
  }, [])

  const initializeAutocomplete = (inputElement, onPlaceSelect) => {
    if (!isLoaded || !window.google || !inputElement) {
      console.log('Cannot initialize autocomplete:', { 
        isLoaded, 
        hasGoogle: !!window.google, 
        hasPlaces: !!window.google?.maps?.places,
        hasInput: !!inputElement 
      })
      return null
    }

    try {
      console.log('Initializing Google Maps Autocomplete...')
      
      // Create autocomplete with gym-specific configuration
      const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
        types: ['establishment'],
        fields: [
          'name',
          'formatted_address',
          'address_components',
          'geometry',
          'place_id',
          'types',
          'website',
          'formatted_phone_number',
          'business_status',
          'rating',
          'opening_hours'
        ],
        // Restrict to India for better results
        componentRestrictions: { country: 'IN' }
      })

      // Set bounds to India for better search results
      const indiaBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(8.4, 68.7),  // Southwest corner of India
        new window.google.maps.LatLng(37.6, 97.25) // Northeast corner of India
      )
      autocomplete.setBounds(indiaBounds)

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        console.log('Place selected:', place)
        
        if (!place.geometry) {
          console.error('No geometry found for place')
          setError('Location not found. Please try a different search term.')
          return
        }

        // Extract place details with better error handling
        try {
          const placeData = {
            name: place.name || '',
            address: place.formatted_address || '',
            placeId: place.place_id || '',
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            types: place.types || [],
            website: place.website || '',
            phone: place.formatted_phone_number || '',
            addressComponents: place.address_components || [],
            businessStatus: place.business_status || '',
            rating: place.rating || null
          }

          // Extract specific address components with better fallback
          const getAddressComponent = (types, fallbackTypes = []) => {
            // Try primary types first
            for (const type of types) {
              const component = place.address_components?.find(comp => 
                comp.types.includes(type)
              )
              if (component) return component.long_name
            }
            
            // Try fallback types
            for (const type of fallbackTypes) {
              const component = place.address_components?.find(comp => 
                comp.types.includes(type)
              )
              if (component) return component.long_name
            }
            
            return ''
          }

          placeData.city = getAddressComponent(
            ['locality'], 
            ['administrative_area_level_2', 'sublocality', 'postal_town']
          )
          placeData.state = getAddressComponent(
            ['administrative_area_level_1'],
            ['administrative_area_level_2']
          )
          placeData.pinCode = getAddressComponent(['postal_code'])
          placeData.country = getAddressComponent(['country'])

          // Determine if it's gym-related
          const gymTypes = ['gym', 'health', 'establishment', 'point_of_interest']
          const isGymRelated = place.types?.some(type => 
            type.includes('gym') || 
            type.includes('fitness') || 
            type.includes('health') ||
            gymTypes.includes(type)
          )
          
          placeData.placeType = isGymRelated ? 'gym' : 'establishment'
          placeData.isGymRelated = isGymRelated

          console.log('Extracted place data:', placeData)
          onPlaceSelect(placeData)
        } catch (extractError) {
          console.error('Error extracting place data:', extractError)
          setError('Error processing location data. Please try manual entry.')
        }
      })

      autocompleteRef.current = autocomplete
      console.log('Autocomplete initialized successfully')
      return autocomplete
    } catch (err) {
      console.error('Error initializing autocomplete:', err)
      setError(`Autocomplete initialization failed: ${err.message}`)
      return null
    }
  }

  const clearAutocomplete = () => {
    if (autocompleteRef.current && window.google?.maps?.event) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
      autocompleteRef.current = null
    }
  }

  // Method to manually search for places
  const searchPlaces = async (query, callback) => {
    if (!isLoaded || !window.google?.maps?.places) {
      callback([], 'Google Maps not loaded')
      return
    }

    try {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      )

      const request = {
        query: query + ' gym fitness',
        fields: ['name', 'formatted_address', 'geometry', 'place_id', 'types'],
        locationBias: { lat: 19.0760, lng: 72.8777 }, // Mumbai as default
      }

      service.textSearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          callback(results, null)
        } else {
          callback([], `Search failed: ${status}`)
        }
      })
    } catch (err) {
      callback([], err.message)
    }
  }

  return {
    isLoaded,
    error,
    initializeAutocomplete,
    clearAutocomplete,
    searchPlaces
  }
} 